import { ApiResponse, ResponseCode } from '@/common/request/types';
import { StringUtils } from '@/common/utils/stringUtils';
import { useAppDialog } from '@/plugins/app-dialog/dialog';
import { Validation } from '@vuelidate/core';
import { Ref } from 'vue';
import { useToast } from 'vue-toastification';

export const MESSAGES = {
    DEFAULT: 'Are you sure you want to do this action?',
    SUCCESS: 'Successfully',
    REMOVE: 'Are you sure you want to remove this item?',
    DISABLE: 'Are you sure you want to disable this item?',
    ENABLE: 'Are you sure you want to enable this item?'
} as const;

// TODO: type safety instead of any | Promise<any>
export interface ActionOptions<TArgs extends unknown[], TData = any> {
    request: (...args: TArgs) => Promise<ApiResponse<TData>>;
    onSuccess?: (response: ApiResponse<TData>) => void | Promise<void | any>;
    onError?: (error: ApiResponse<TData>) => void | Promise<void | any>;
    onFinally?: () => void | any | Promise<void | any>;
    canExecute?: () => boolean | Promise<boolean>;
    validator?: Ref<Validation>;
    confirmMessage?: string;
    successMessage?: string;
}

export function useCrudActions() {
    const dialog = useAppDialog();
    const toast = useToast();

    const createAction = <TArgs extends unknown[], TData = any>(options: ActionOptions<TArgs, TData>) => {
        const validate = async () => {
            if ((await options.canExecute?.()) === false) {
                return false;
            }

            options.validator?.value.$touch();
            return !options.validator?.value.$invalid;
        };

        const executeApi = async (...args: TArgs) => {
            try {
                const response = await options.request(...args);
                if (response.code === ResponseCode.Ok) {
                    if (!StringUtils.isEmpty(options.successMessage)) {
                        toast.success(options.successMessage);
                    }

                    await options.onSuccess?.(response);
                } else {
                    await options.onError?.(response);
                }

                return response;
            } catch (error: any) {
                await options.onError?.(error);
                return error;
            } finally {
                await options.onFinally?.();
            }
        };

        return async (...args: TArgs) => {
            if (!(await validate())) {
                return;
            }

            if (options.confirmMessage) {
                const confirmed = await dialog.confirm({ message: options.confirmMessage });
                if (!confirmed) {
                    return;
                }
            }

            return await executeApi(...args);
        };
    };

    const withNotify =
        (successMessage: string) =>
        <TArgs extends unknown[], TData = any>(options: ActionOptions<TArgs, TData>) =>
            createAction({
                successMessage: successMessage,
                ...options
            });

    const withConfirmAndNotify =
        (confirmMessage: string, successMessage: string = MESSAGES.SUCCESS) =>
        <TArgs extends unknown[], TData = any>(options: ActionOptions<TArgs, TData>) =>
            withNotify(successMessage)({
                confirmMessage: confirmMessage,
                ...options
            });

    return {
        executeAction: createAction,
        excuteAndNotify: withNotify(MESSAGES.SUCCESS),
        confirmAndExecute: withConfirmAndNotify(MESSAGES.DEFAULT),
        confirmAndRemove: withConfirmAndNotify(MESSAGES.REMOVE),
        confirmAndDisable: withConfirmAndNotify(MESSAGES.DISABLE),
        confirmAndEnable: withConfirmAndNotify(MESSAGES.ENABLE)
    };
}
